import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Tooltip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Divider,
  Menu,
  ListItemAvatar,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  People,
  Person,
  PersonAdd,
  Settings,
  MoreVert,
  ExpandMore,
  Security,
  AdminPanelSettings,
  Group,
  Visibility,
  Star,
  CheckCircle,
  Cancel,
  Search,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

interface Team {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  member_count: number;
  creator?: {
    id: number;
    email: string;
  };
  settings?: {
    is_public?: boolean;
    auto_approve_members?: boolean;
    allow_member_invites?: boolean;
  };
}

interface TeamMember {
  id: number;
  user_id: number;
  team_id: number;
  role: 'member' | 'admin' | 'owner';
  joined_at: string;
  is_active: boolean;
  user: {
    id: number;
    email: string;
    role: string;
    is_active: boolean;
  };
}

interface User {
  id: number;
  email: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

const TeamManagement: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [teamSearchQuery, setTeamSearchQuery] = useState('');

  const [newTeam, setNewTeam] = useState({
    name: '',
    description: '',
    is_public: false,
    auto_approve_members: true,
    allow_member_invites: false
  });

  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [memberRole, setMemberRole] = useState<'member' | 'admin'>('member');

  useEffect(() => {
    fetchTeams();
    fetchAvailableUsers();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/teams');
      setTeams(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to fetch teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async (teamId: number) => {
    try {
      const response = await axios.get(`/api/teams/${teamId}`);
      setTeamMembers(response.data.data.members || []);
    } catch (error: any) {
      console.error('Failed to fetch team members:', error);
      toast.error('Failed to load team members');
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await axios.get('/api/organizations/me/members');
      // Transform organization members to match User interface
      const users = (response.data.members || []).map((member: any) => ({
        id: member.User?.id || member.user_id,
        email: member.User?.email || 'Unknown',
        role: member.User?.role || 'user',
        is_active: member.User?.is_active ?? true,
        created_at: member.User?.created_at || new Date().toISOString()
      }));
      setAvailableUsers(users);
    } catch (error: any) {
      console.error('Failed to fetch available users:', error);
    }
  };

  const createTeam = async () => {
    try {
      const response = await axios.post('/api/teams', newTeam);
      
      toast.success('Team created successfully!');
      setCreateDialogOpen(false);
      setNewTeam({
        name: '',
        description: '',
        is_public: false,
        auto_approve_members: true,
        allow_member_invites: false
      });
      
      fetchTeams();
    } catch (error: any) {
      console.error('Failed to create team:', error);
      toast.error(error.response?.data?.message || 'Failed to create team');
    }
  };

  const updateTeam = async () => {
    if (!selectedTeam) return;
    
    try {
      await axios.put(`/api/teams/${selectedTeam.id}`, {
        name: selectedTeam.name,
        description: selectedTeam.description,
        is_active: selectedTeam.is_active
      });
      
      toast.success('Team updated successfully!');
      setEditDialogOpen(false);
      fetchTeams();
    } catch (error: any) {
      console.error('Failed to update team:', error);
      toast.error(error.response?.data?.message || 'Failed to update team');
    }
  };

  const deleteTeam = async (teamId: number) => {
    if (!window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/teams/${teamId}`);
      toast.success('Team deleted successfully');
      fetchTeams();
    } catch (error: any) {
      console.error('Failed to delete team:', error);
      toast.error(error.response?.data?.message || 'Failed to delete team');
    }
  };

  const addMembersToTeam = async () => {
    if (!selectedTeam || selectedUsers.length === 0) return;

    try {
      const response = await axios.post(`/api/teams/${selectedTeam.id}/members`, {
        user_ids: selectedUsers,
        role: memberRole
      });

      const { data } = response.data;
      const successful = data.successful || [];
      const failed = data.failed || [];

      if (successful.length > 0) {
        toast.success(`Successfully added ${successful.length} member(s) to team`);
      }
      
      if (failed.length > 0) {
        const failedMessages = failed.map((f: any) => 
          `User ${f.user_id}: ${f.error}`
        ).join(', ');
        toast.error(`Failed to add ${failed.length} member(s): ${failedMessages}`);
      }

      setAddMemberDialogOpen(false);
      setSelectedUsers([]);
      setMemberRole('member');
      fetchTeamMembers(selectedTeam.id);
    } catch (error: any) {
      console.error('Failed to add members:', error);
      toast.error(error.response?.data?.message || 'Failed to add members');
    }
  };

  const updateMemberRole = async (userId: number, newRole: string) => {
    if (!selectedTeam) return;

    try {
      await axios.put(`/api/teams/${selectedTeam.id}/members/${userId}/role`, {
        role: newRole
      });

      toast.success('Member role updated successfully');
      fetchTeamMembers(selectedTeam.id);
    } catch (error: any) {
      console.error('Failed to update member role:', error);
      toast.error(error.response?.data?.message || 'Failed to update member role');
    }
  };

  const removeMember = async (userId: number) => {
    if (!selectedTeam) return;
    
    if (!window.confirm('Are you sure you want to remove this member from the team?')) {
      return;
    }

    try {
      await axios.delete(`/api/teams/${selectedTeam.id}/members/${userId}`);
      toast.success('Member removed from team');
      fetchTeamMembers(selectedTeam.id);
    } catch (error: any) {
      console.error('Failed to remove member:', error);
      toast.error(error.response?.data?.message || 'Failed to remove member');
    }
  };

  const updateTeamSettings = async () => {
    if (!selectedTeam) return;

    try {
      await axios.put(`/api/teams/${selectedTeam.id}/settings`, selectedTeam.settings);
      toast.success('Team settings updated successfully');
      setSettingsDialogOpen(false);
      fetchTeams();
    } catch (error: any) {
      console.error('Failed to update team settings:', error);
      toast.error(error.response?.data?.message || 'Failed to update settings');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'error';
      case 'admin': return 'warning';
      case 'member': return 'info';
      default: return 'default';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <Star />;
      case 'admin': return <AdminPanelSettings />;
      case 'member': return <Person />;
      default: return <Person />;
    }
  };

  const filteredUsers = availableUsers.filter(u => {
    const matchesSearch = searchQuery === '' || 
      u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.role.toLowerCase().includes(searchQuery.toLowerCase());
    
    const notAlreadyMember = !teamMembers.some(member => 
      member.user?.id === u.id || member.user_id === u.id
    );
    
    return matchesSearch && notAlreadyMember;
  });

  const filteredTeamMembers = teamMembers.filter(member => {
    if (memberSearchQuery === '') return true;
    
    const email = member.user?.email || '';
    const role = member.role || '';
    const userId = member.user?.id || member.user_id || '';
    
    return email.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
           role.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
           userId.toString().includes(memberSearchQuery);
  });

  const filteredTeams = teams.filter(team => {
    if (teamSearchQuery === '') return true;
    
    const name = team.name || '';
    const description = team.description || '';
    
    return name.toLowerCase().includes(teamSearchQuery.toLowerCase()) ||
           description.toLowerCase().includes(teamSearchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Team Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Team
        </Button>
      </Box>

      <Typography variant="body1" color="textSecondary" paragraph>
        Create and manage teams within your organization. Organize users into teams for better collaboration and access control.
      </Typography>

      {/* Team Search */}
      {teams.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Search Teams"
            placeholder="Search by team name or description..."
            value={teamSearchQuery}
            onChange={(e) => setTeamSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            helperText={teamSearchQuery && `Found ${filteredTeams.length} team(s)`}
          />
        </Box>
      )}

      {/* Teams Grid */}
      <Grid container spacing={3}>
        {teams.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Group sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Teams Created Yet
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Create your first team to start organizing your organization members.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create First Team
              </Button>
            </Paper>
          </Grid>
        ) : filteredTeams.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Search sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Teams Found
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Try adjusting your search criteria.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setTeamSearchQuery('')}
              >
                Clear Search
              </Button>
            </Paper>
          </Grid>
        ) : (
          filteredTeams.map((team) => (
            <Grid item xs={12} sm={6} md={4} key={team.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {team.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {team.description || 'No description'}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setMenuAnchor(e.currentTarget);
                        setSelectedTeam(team);
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <People fontSize="small" />
                    <Typography variant="body2">
                      {team.member_count} member{team.member_count !== 1 ? 's' : ''}
                    </Typography>
                    <Chip
                      label={team.is_active ? 'Active' : 'Inactive'}
                      color={team.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => {
                        setSelectedTeam(team);
                        fetchTeamMembers(team.id);
                        setMembersDialogOpen(true);
                      }}
                    >
                      View Members
                    </Button>
                    <Button
                      size="small"
                      startIcon={<PersonAdd />}
                      onClick={() => {
                        setSelectedTeam(team);
                        fetchAvailableUsers(); // Refresh available users
                        setAddMemberDialogOpen(true);
                      }}
                    >
                      Add Members
                    </Button>
                  </Box>

                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Created: {new Date(team.created_at).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Team Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setEditDialogOpen(true);
            setMenuAnchor(null);
          }}
        >
          <Edit sx={{ mr: 1 }} />
          Edit Team
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedTeam) {
              fetchTeamMembers(selectedTeam.id);
              setMembersDialogOpen(true);
            }
            setMenuAnchor(null);
          }}
        >
          <People sx={{ mr: 1 }} />
          Manage Members
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSettingsDialogOpen(true);
            setMenuAnchor(null);
          }}
        >
          <Settings sx={{ mr: 1 }} />
          Team Settings
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (selectedTeam) {
              deleteTeam(selectedTeam.id);
            }
            setMenuAnchor(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} />
          Delete Team
        </MenuItem>
      </Menu>

      {/* Create Team Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Team</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Team Name"
                value={newTeam.name}
                onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={newTeam.description}
                onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Team Settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={newTeam.is_public}
                    onChange={(e) => setNewTeam({ ...newTeam, is_public: e.target.checked })}
                  />
                }
                label="Public Team (visible to all organization members)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newTeam.auto_approve_members}
                    onChange={(e) => setNewTeam({ ...newTeam, auto_approve_members: e.target.checked })}
                  />
                }
                label="Auto-approve new members"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newTeam.allow_member_invites}
                    onChange={(e) => setNewTeam({ ...newTeam, allow_member_invites: e.target.checked })}
                  />
                }
                label="Allow members to invite others"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={createTeam} variant="contained" disabled={!newTeam.name}>
            Create Team
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Team</DialogTitle>
        <DialogContent>
          {selectedTeam && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Team Name"
                  value={selectedTeam.name}
                  onChange={(e) => setSelectedTeam({ ...selectedTeam, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={selectedTeam.description || ''}
                  onChange={(e) => setSelectedTeam({ ...selectedTeam, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedTeam.is_active}
                      onChange={(e) => setSelectedTeam({ ...selectedTeam, is_active: e.target.checked })}
                    />
                  }
                  label="Team Active"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={updateTeam} variant="contained">
            Update Team
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Members Dialog */}
      <Dialog open={membersDialogOpen} onClose={() => {
        setMembersDialogOpen(false);
        setMemberSearchQuery('');
      }} maxWidth="md" fullWidth>
        <DialogTitle>
          Team Members - {selectedTeam?.name}
        </DialogTitle>
        <DialogContent>
          {/* Search Bar */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Search Members"
              placeholder="Search by email, role, or user ID..."
              value={memberSearchQuery}
              onChange={(e) => setMemberSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              helperText={memberSearchQuery && `Found ${filteredTeamMembers.length} member(s)`}
            />
          </Box>

          {teamMembers.length === 0 ? (
            <Box textAlign="center" py={4}>
              <People sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Members Yet
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Add members to this team to get started.
              </Typography>
            </Box>
          ) : filteredTeamMembers.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Search sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Members Found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Try adjusting your search criteria.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTeamMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar>
                            <Person />
                          </Avatar>
                          <Box>
                            <Typography variant="body2">{member.user?.email || 'Unknown User'}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              User ID: {member.user?.id || member.user_id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getRoleIcon(member.role)}
                          label={member.role.toUpperCase()}
                          color={getRoleColor(member.role) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(member.joined_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={member.is_active ? 'Active' : 'Inactive'}
                          color={member.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={member.role}
                              onChange={(e) => updateMemberRole(member.user?.id || member.user_id, e.target.value)}
                              disabled={member.role === 'owner'}
                            >
                              <MenuItem value="member">Member</MenuItem>
                              <MenuItem value="admin">Admin</MenuItem>
                              {member.role === 'owner' && <MenuItem value="owner">Owner</MenuItem>}
                            </Select>
                          </FormControl>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeMember(member.user?.id || member.user_id)}
                            disabled={member.role === 'owner'}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<PersonAdd />}
            onClick={() => setAddMemberDialogOpen(true)}
          >
            Add Members
          </Button>
          <Button onClick={() => setMembersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Members Dialog */}
      <Dialog open={addMemberDialogOpen} onClose={() => setAddMemberDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Members to {selectedTeam?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Search Users"
                placeholder="Search by email or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                helperText={searchQuery && `Found ${filteredUsers.length} user(s)`}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Default Role</InputLabel>
                <Select
                  value={memberRole}
                  onChange={(e) => setMemberRole(e.target.value as 'member' | 'admin')}
                >
                  <MenuItem value="member">Member</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Available Users ({filteredUsers.length})
              </Typography>
              <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {filteredUsers.length === 0 ? (
                  <Box p={2} textAlign="center">
                    <Typography variant="body2" color="textSecondary">
                      No available users found
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {filteredUsers.map((user) => (
                      <ListItem
                        key={user.id}
                        button
                        onClick={() => {
                          if (selectedUsers.includes(user.id)) {
                            setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                          } else {
                            setSelectedUsers([...selectedUsers, user.id]);
                          }
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar>
                            <Person />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={user.email}
                          secondary={`Role: ${user.role} • Joined: ${new Date(user.created_at).toLocaleDateString()}`}
                        />
                        <ListItemSecondaryAction>
                          {selectedUsers.includes(user.id) ? (
                            <CheckCircle color="primary" />
                          ) : (
                            <Cancel color="disabled" />
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
              {selectedUsers.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {selectedUsers.length} user(s) selected to add as {memberRole}(s)
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={addMembersToTeam}
            variant="contained"
            disabled={selectedUsers.length === 0}
          >
            Add {selectedUsers.length} Member(s)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Settings Dialog */}
      <Dialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Team Settings - {selectedTeam?.name}</DialogTitle>
        <DialogContent>
          {selectedTeam && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Team Visibility & Access
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedTeam.settings?.is_public || false}
                      onChange={(e) => setSelectedTeam({
                        ...selectedTeam,
                        settings: { 
                          auto_approve_members: false,
                          allow_member_invites: false,
                          ...selectedTeam.settings, 
                          is_public: e.target.checked 
                        }
                      })}
                    />
                  }
                  label="Public Team"
                />
                <Typography variant="caption" color="textSecondary" display="block">
                  Public teams are visible to all organization members
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Member Management
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedTeam.settings?.auto_approve_members || false}
                      onChange={(e) => setSelectedTeam({
                        ...selectedTeam,
                        settings: { 
                          is_public: false,
                          allow_member_invites: false,
                          ...selectedTeam.settings, 
                          auto_approve_members: e.target.checked 
                        }
                      })}
                    />
                  }
                  label="Auto-approve Members"
                />
                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                  Automatically approve join requests
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedTeam.settings?.allow_member_invites || false}
                      onChange={(e) => setSelectedTeam({
                        ...selectedTeam,
                        settings: { 
                          is_public: false,
                          auto_approve_members: false,
                          ...selectedTeam.settings, 
                          allow_member_invites: e.target.checked 
                        }
                      })}
                    />
                  }
                  label="Allow Member Invites"
                />
                <Typography variant="caption" color="textSecondary" display="block">
                  Allow team members to invite others
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
          <Button onClick={updateTeamSettings} variant="contained">
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamManagement; 